import { Suspense } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { Header, Navbar, Sidebar } from 'src/components/elements'
import CompareBar from 'src/components/elements/compare/CompareBar'
import { Loader } from 'src/components/ui'
import { useGetUserQuery } from 'src/redux/api'
import { isSignedOutError } from 'src/helpers/authState'
import ServerUnavailable from 'src/components/elements/server-unavailable/ServerUnavailable'
import Footer from 'src/components/elements/footer/Footer'
import { SidebarProvider, useSidebar } from 'src/contexts/SidebarContext'
import clsx from 'clsx'

const MainLayoutContent = () => {
  const { data: user, isLoading, error, refetch } = useGetUserQuery(undefined)
  const { collapsed } = useSidebar()
  // The map spreads across the whole working area — a width cap there wastes
  // screen; regular pages keep the readable 82rem column. The map is also the
  // front page (38.3), so "/" is full-width too.
  const pathname = useLocation().pathname
  const isFullWidthPage = pathname.startsWith('/map') || pathname === '/'
  // the listing wizard is a task with its own action bar: no footer under it
  const isWizard = pathname.startsWith('/create-ad') || pathname.startsWith('/edit-ad')

  if (isLoading) return <Loader />
  // Only a real sign-out leads to /login. A failed refetch keeps the cached
  // user on the page; a server that is down on the first load gets a retry.
  if (!user && (!error || isSignedOutError(error)))
    return (
      <Navigate to="/login" state={{ from: window.location.pathname + window.location.search }} />
    )
  if (!user) return <ServerUnavailable onRetry={refetch} />

  return (
    <>
      <div
        className={clsx(
          // floating bars (compare, the listing's contact button) clear the menu
          'relative transition-all duration-300 max-md:[--bottom-bar-h:var(--navbar-h)] max-md:[--top-bar-h:var(--header-h)]',
          collapsed ? 'md:pl-[5rem]' : 'md:pl-[23rem]',
        )}
      >
        <div className="fixed top-0 left-0 z-20 h-full overflow-y-auto sidebar-scroll md:block hidden">
          <Sidebar />
        </div>
        {/* phones: room for the fixed header above and the menu below; at
            least the visible height (dvh: 100vh on iOS counts the hidden
            Safari toolbar and pushed the footer under it) */}
        <main className="flex flex-col min-h-screen supports-[height:100dvh]:min-h-dvh md:flex-1 bg-page pt-[calc(var(--header-h)+1rem)] pb-[calc(var(--navbar-h)+2rem)] md:py-0">
          <div
            className={clsx(
              'flex flex-col gap-4 px-4 md:p-8 md:py-9 relative w-full md:mx-auto',
              isFullWidthPage ? 'md:max-w-none' : 'md:max-w-[82rem] xl:max-w-[96rem]',
            )}
          >
            <Header />
            {/* Suspense wraps the routed page only — around the whole shell a
                lazy page switch blanked out the sidebar and the header too. */}
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
            <Navbar className="md:hidden" />
          </div>
          {/* every scrolling page ends with the footer; the map fills the screen */}
          {!isFullWidthPage && !isWizard && (
            <div className="mt-auto pt-8">
              <Footer authed />
            </div>
          )}
        </main>
        <CompareBar />
      </div>
    </>
  )
}

const MainLayout = () => {
  return (
    <SidebarProvider>
      <MainLayoutContent />
    </SidebarProvider>
  )
}

export default MainLayout
